import { useState, useEffect } from 'react';
import { parseISO, format } from 'date-fns/fp';
import Cookies from 'js-cookie';
import { getOr, get, flow, first, isEmpty, capitalize, includes, find, replace, keyBy, mapValues, merge } from 'lodash/fp';
import Smooch from 'smooch';

const COOKIE_NAME = 'persist-chat';
const SESSION_KEYS = ['appUserId', 'clientId', 'sessionToken'];
function updateConversationDisplayName(displayName) {
    return conversations => {
        const conversation = conversations[0];
        const conversationId = get('_id')(conversation);
        if (conversationId) {
            Smooch.updateConversation(conversationId, { displayName });
        }
    };
}
function useWebChat({ webChatContent, profile, booking, propertyCode, localeIsoCode = 'en-US', options, }) {
    const [initialized, setInitialized] = useState(false);
    const enabled = getOr(false, 'enabled')(webChatContent);
    const integrationId = get('integrationId')(webChatContent);
    const businessIconUrl = get(['propertyInfo', 'image', 'url'])(webChatContent);
    const bookingId = get('bookingId')(booking);
    const reservation = flow(get('hotelProducts'), first)(booking);
    const reservationId = getOr('', 'reservationId')(reservation);
    const jwt = get('webChatToken')(profile) || flow(get('customers'), first, get('webChatToken'))(booking);
    const externalId = get('orsNameId')(profile) || flow(get('customers'), first, get('orsNameId'))(booking);
    const title = flow(({ profile: p, booking: b }) => {
        if (!isEmpty(p))
            return get(['name', 'title'])(p);
        return get(['contactDetails', 'name', 'title'])(b);
    }, capitalize, profileTitle => {
        if (includes('.')(profileTitle))
            return profileTitle;
        return `${profileTitle}.`;
    })({ profile, booking });
    const givenName = get(['name', 'firstName'])(profile) || get(['contactDetails', 'name', 'firstName'])(booking);
    const surname = get(['name', 'surname'])(profile) || get(['contactDetails', 'name', 'surname'])(booking);
    const email = flow(get('emails'), find(({ activeEmail, defaultEmail }) => activeEmail || defaultEmail), get('email'))(profile) || get(['contactDetails', 'email'])(booking);
    const phone = flow(get('phones'), find(({ activeEmail, defaultEmail }) => activeEmail || defaultEmail), get('number'))(profile) || get(['contactDetails', 'phones', '0', 'number'])(booking);
    const propertyNameRaw = getOr('', ['propertyInfo', 'name'])(webChatContent);
    const propertyNameIncludesHtml = getOr(false, ['propertyInfo', 'nameIncludesHtml'])(webChatContent);
    const propertyName = propertyNameIncludesHtml
        ? replace(/(<([^>]+)>)/gi, '')(propertyNameRaw)
        : propertyNameRaw;
    const getCustomText = () => flow(getOr([], 'translations'), keyBy('key'), mapValues(({ value }) => value))(webChatContent);
    const smoochProperties = {
        integrationId,
        businessName: propertyName,
        browserStorage: 'sessionStorage',
        businessIconUrl,
        locale: localeIsoCode,
        customText: getCustomText(),
        integrationOrder: [],
        customColors: {
            brandColor: '000000',
            conversationColor: '000000',
            actionColor: '000000',
        },
        canUserSeeConversationList: false,
        menuItems: {
            imageUpload: true,
            fileUpload: true,
            shareLocation: true,
        },
    };
    const setPredefinedMessage = message => {
        if ((Smooch === null || Smooch === void 0 ? void 0 : Smooch.setPredefinedMessage) && message) {
            Smooch.setPredefinedMessage(message);
        }
    };
    const handleSmoochEvents = () => {
        const emptyFunction = () => undefined;
        Smooch.on('destroy', (options === null || options === void 0 ? void 0 : options.onDestroy) || emptyFunction);
        Smooch.on('widget:opened', (options === null || options === void 0 ? void 0 : options.onWidgetOpen) || emptyFunction);
        Smooch.on('widget:closed', (options === null || options === void 0 ? void 0 : options.onWidgetClose) || emptyFunction);
        Smooch.on('message:sent', (options === null || options === void 0 ? void 0 : options.onMessageSent) || emptyFunction);
    };
    const fixPropertyNameWithHtml = () => {
        var _a;
        const webChatContainer = document.getElementById('web-messenger-container');
        const properyNameDiv = (_a = webChatContainer === null || webChatContainer === void 0 ? void 0 : webChatContainer.contentWindow) === null || _a === void 0 ? void 0 : _a.document.getElementsByClassName('app-name')[0];
        if (properyNameDiv) {
            properyNameDiv.innerHTML = propertyNameRaw;
        }
    };
    const hideButton = () => {
        var _a;
        const webChatContainer = document.getElementById('web-messenger-container');
        const webChatButton = (_a = webChatContainer === null || webChatContainer === void 0 ? void 0 : webChatContainer.contentWindow) === null || _a === void 0 ? void 0 : _a.document.getElementById('messenger-button');
        if (webChatButton) {
            webChatButton.style.visibility = 'hidden';
        }
    };
    const handleSmoochEnabled = () => {
        if (options === null || options === void 0 ? void 0 : options.hideFloatingChatButton)
            hideButton();
        if (propertyNameIncludesHtml)
            fixPropertyNameWithHtml();
        if (options === null || options === void 0 ? void 0 : options.onReady)
            options.onReady();
        handleSmoochEvents();
        setInitialized(true);
    };
    const openWebChat = () => {
        var _a;
        if (initialized && (Smooch === null || Smooch === void 0 ? void 0 : Smooch.open) && !((_a = Smooch === null || Smooch === void 0 ? void 0 : Smooch.isOpened) === null || _a === void 0 ? void 0 : _a.call(Smooch))) {
            Smooch.open();
        }
    };
    const beforeDisplay = (message, _data) => {
        var _a;
        if (message.deleted)
            return null;
        if (!message.metadata)
            return null;
        return ((_a = message.metadata) === null || _a === void 0 ? void 0 : _a.property_code) === propertyCode ? message : null;
    };
    const beforeSend = (message, data) => {
        var _a;
        saveSessionToCookie({ jwt, reservation, integrationId });
        const metadataFromMessage = data.metadata;
        const AXPMetadata = {
            property_name: propertyName,
            property_code: propertyCode,
            webpage_url: window.location.href,
        };
        const reservationData = reservationId
            ? {
                propertyName,
                confirmationNumber: reservationId,
                stayDates: [get('checkInDate')(reservation), get('checkOutDate')(reservation)]
                    .filter(Boolean)
                    .map(parseISO)
                    .map(format("MMM d',' yyyy"))
                    .join(' - '),
            }
            : {};
        const metadata = flow(merge(AXPMetadata), merge(reservationData), merge((_a = options === null || options === void 0 ? void 0 : options.generateAdditionalMetadata) === null || _a === void 0 ? void 0 : _a.call(options)))(metadataFromMessage);
        return Object.assign(Object.assign({}, message), { metadata });
    };
    useEffect(() => {
        if (enabled && integrationId) {
            writeSessionFromCookie({ jwt, integrationId });
            Smooch.init(smoochProperties).then(handleSmoochEnabled).catch(console.log);
        }
        return () => {
            setInitialized(false);
            Smooch.destroy();
        };
    }, [enabled, integrationId]);
    useEffect(() => {
        if (initialized && (Smooch === null || Smooch === void 0 ? void 0 : Smooch.setDelegate)) {
            Smooch.setDelegate({ beforeDisplay, beforeSend });
        }
    }, [initialized, email, bookingId]);
    useEffect(() => {
        if (initialized && email && (Smooch === null || Smooch === void 0 ? void 0 : Smooch.updateUser)) {
            Smooch.updateUser({
                givenName,
                surname,
                email,
                properties: {
                    'profile.id': externalId,
                    'profile.title': title,
                    'profile.phone': phone,
                },
            });
        }
    }, [initialized, email]);
    useEffect(() => {
        if (initialized && jwt && externalId) {
            Smooch.login(externalId, jwt)
                .then(flow(get('conversations'), updateConversationDisplayName(propertyName)))
                .catch(err => {
                // do nothing
            });
        }
    }, [initialized, jwt, externalId]);
    return {
        enabled,
        initialized,
        openWebChat,
        setPredefinedMessage,
    };
}
const writeSessionFromCookie = ({ jwt, integrationId }) => {
    if (!jwt) {
        // if there isn't a JWT check if there's a persist-chat cookie and pull in those params
        const sessionCookie = Cookies.get(COOKIE_NAME);
        if (sessionCookie) {
            try {
                //parse out the session data
                const jsonSessionData = JSON.parse(sessionCookie);
                SESSION_KEYS.forEach(key => {
                    sessionStorage.setItem(`${integrationId}.${key}`, jsonSessionData[key]);
                });
            }
            catch (e) {
                // do nothing
            }
        }
    }
};
const saveSessionToCookie = ({ jwt, reservation, integrationId }) => {
    var _a, _b;
    // if this user isn't signed in, has a booking available, and functional cookies are enabled, capture all the smooch session info
    if (!jwt && reservation) {
        (_b = (_a = window['FS']) === null || _a === void 0 ? void 0 : _a.GDPR) === null || _b === void 0 ? void 0 : _b.ifAllowed('functional', function () {
            const chatSession = SESSION_KEYS.reduce((sessionData, key) => (Object.assign(Object.assign({}, sessionData), { [key]: sessionStorage.getItem(`${integrationId}.${key}`) })), {});
            // make sure we've got all the data
            if (!Object.values(chatSession).filter(val => !val).length) {
                const hostParts = window.location.host.split('.');
                const domain = hostParts.length > 1
                    ? `.${hostParts[hostParts.length - 2]}.${hostParts[hostParts.length - 1]}`
                    : hostParts[0];
                // expiration is 20 minutes. 1 is a day. 24 hours * 3 20 minute chunks per hour
                Cookies.set(COOKIE_NAME, JSON.stringify(chatSession), { domain, expires: 1 / 72 });
            }
        });
    }
};

export { useWebChat };
